// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Dialog,
  type DialogProps,
  Modal,
  type ModalProps,
  useMediaQuery,
  useTheme,
  type Breakpoint,
} from '@mui/material';
import {
  type ComponentType,
  type ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { isDefined } from '@shared/utils';

/**
 * Props passed to the optional Trigger component that opens the modal/dialog. Use a
 * button or any clickable element that receives these props.
 */
export type TriggerProps = {
  /** Called when the trigger is clicked; opens the modal/dialog. */
  onClick?: () => void;
  /** When true, the trigger should be disabled (e.g. not open the modal). */
  disabled?: boolean;
  /** When false, the trigger is treated as disabled (validation state). */
  valid?: boolean;
  /** When true, the trigger may show a loading state. */
  loading?: boolean;
};

/** Options to configure the modal hook: component type and lifecycle callbacks. */
export interface ModalOptions {
  /** Whether the modal/dialog is open on first render. Defaults to `false`. */
  defaultOpen?: boolean;
  /** Called after the modal/dialog has closed. */
  afterClose?: () => void;
  /** Called before the modal/dialog starts closing. */
  beforeClose?: () => void;
  /** Called before the modal/dialog starts opening. */
  beforeOpen?: () => void;
  /** Called after the modal/dialog has opened. */
  afterOpen?: () => void;
  /** Which MUI component to use: `'modal'` (base Modal) or `'dialog'` (Dialog). */
  component?: 'dialog' | 'modal';
  /**
   * When `component` is `dialog`, set MUI `fullScreen` below this breakpoint
   * (aligns with Tailwind `lg:` etc.).
   */
  fullScreenBelow?: Breakpoint;
}

/** Default options for {@link useModal}. */
export const DEFAULT_MODAL_OPTIONS = {
  defaultOpen: false,
  component: 'modal',
} satisfies ModalOptions;

/**
 * Hook for a controlled MUI Modal or Dialog with optional trigger element.
 *
 * Manages open/close state and exposes a `Renderer` component that renders an
 * optional Trigger (e.g. button) and either a base
 * {@link https://mui.com/material-ui/api/modal/ | Modal} or a
 * {@link https://mui.com/material-ui/api/dialog/ | Dialog}. When
 * `component="dialog"`, backdrop click does not close the dialog (only explicit
 * close or escape).
 *
 * @example With Dialog and a button trigger
 *
 * ```tsx
 * const [ModalRenderer, { open, onClose, onOpen }] = useModal({
 *   component: 'dialog',
 * });
 *
 * <ModalRenderer
 *   Trigger={({ onClick }) => <Button onClick={onClick}>Open</Button>}
 *   title="My Dialog"
 * >
 *   <DialogContent>Content</DialogContent>
 * </ModalRenderer>;
 * ```
 *
 * @example Without trigger (manual control)
 *
 * ```tsx
 * const [ModalRenderer, { onOpen, onClose }] = useModal();
 *
 * <ModalRenderer>Content</ModalRenderer>
 * <Button onClick={onOpen}>Open modal</Button>
 * ```
 *
 * @param options - Configuration and lifecycle callbacks. Defaults to
 *   {@link DEFAULT_MODAL_OPTIONS}.
 * @returns A tuple `[Renderer, controls]`:
 *
 *   - `Renderer`: Component that accepts optional `Trigger`, `triggerDisabled`,
 *       `triggerValid`, `triggerLoading`, and any MUI Modal/Dialog props (except
 *       `open`/`onClose`).
 *   - `controls`: `{ open, onClose, onOpen, onCloseDialog }` for imperative control.
 *       Use `onCloseDialog` when passing to Dialog's `onClose` (handles reason).
 */
// istanbul ignore next
export const useModal = ({
  defaultOpen = false,
  component = 'modal',
  fullScreenBelow,
  afterClose,
  beforeClose,
  beforeOpen,
  afterOpen,
}: ModalOptions = DEFAULT_MODAL_OPTIONS) => {
  const [open, setOpen] = useState(defaultOpen!);
  const openRef = useRef(open);
  openRef.current = open;

  const theme = useTheme();
  const isFullScreenBelow = useMediaQuery(
    isDefined(fullScreenBelow)
      ? theme.breakpoints.down(fullScreenBelow)
      : '(max-width: -1px)',
    { noSsr: true },
  );
  const responsiveFullScreen =
    component === 'dialog' && isDefined(fullScreenBelow) && isFullScreenBelow;

  const Component = useMemo(
    () => (component === 'dialog' ? Dialog : Modal),
    [component],
  );

  const onClose = useCallback(() => {
    beforeClose?.();

    setOpen(false);

    afterClose?.();
  }, [afterClose, beforeClose]);

  // istanbul ignore next
  const onCloseDialog: Required<DialogProps>['onClose'] = useCallback(
    (_, reason) => {
      if (reason && reason === 'backdropClick') return;

      onClose();
    },
    [onClose],
  );

  const onOpen = useCallback(() => {
    beforeOpen?.();

    setOpen(true);

    afterOpen?.();
  }, [afterOpen, beforeOpen]);

  const onCloseDialogRef = useRef(onCloseDialog);
  const onOpenRef = useRef(onOpen);
  onCloseDialogRef.current = onCloseDialog;
  onOpenRef.current = onOpen;

  const onModalCloseDialog = useCallback(
    ((...args) =>
      onCloseDialogRef.current(...args)) as Required<DialogProps>['onClose'],
    [],
  );
  const onModalOpen = useCallback(() => onOpenRef.current(), []);

  const Renderer = useCallback(
    ({
      Trigger,
      triggerDisabled,
      triggerValid,
      triggerLoading,
      fullScreen: fullScreenProp,
      ...modalProps
    }: PropsWithChildren<
      Omit<ModalProps & DialogProps, 'open' | 'children'> & {
        Trigger?: ComponentType<TriggerProps>;
        triggerDisabled?: boolean;
        triggerValid?: boolean;
        triggerLoading?: boolean;
      }
    >) => (
      <>
        {Trigger && (
          <Trigger
            onClick={onModalOpen}
            disabled={triggerDisabled || triggerValid === false}
            valid={triggerValid}
            loading={triggerLoading}
          />
        )}
        <Component
          open={openRef.current}
          onClose={modalProps.onClose ?? onModalCloseDialog}
          fullScreen={fullScreenProp ?? responsiveFullScreen}
          {...modalProps}
        >
          {modalProps?.children as ReactElement}
        </Component>
      </>
    ),
    [Component, onModalCloseDialog, onModalOpen, responsiveFullScreen],
  );

  return [
    Renderer,
    {
      open,
      onClose,
      onOpen,
      onCloseDialog,
    },
  ] as const;
};
